import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Avatar,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  BlockOutlined,
  EditOutlined,
  LockOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { supabase } from '@/lib/supabase'
import { resolveAvatarUrl } from '@/lib/avatar'
import { formatCST, pickerToUTCISO } from '@/lib/time'
import { useIsMobile } from '@/lib/hooks'
import { AdminFilterBar } from '@/components/AdminFilterBar'
import { AdminMobileList } from '@/components/AdminMobileList'
import { UserBadgesPanel } from '@/components/UserBadgesPanel'

export const Route = createFileRoute('/_authed/users')({
  component: UsersPage,
})

/* ── 类型 ───────────────────────────────────────────────────── */

type UserStatus = 'active' | 'suspended' | 'banned'
type RoleFilter = 'moderator' | 'core' | 'normal'
/** Matches admin.list_users p_membership filters. */
type MembershipFilter = 'vip' | 'permanent' | 'expired' | 'non_vip'
type SortKey = 'created_desc' | 'last_sign_in_desc' | 'username_asc'
type MembershipMode = 'add_days' | 'set_until' | 'set_permanent' | 'clear'

interface UserRow {
  id: string
  username: string | null
  display_name: string | null
  display: string
  avatar?: unknown
  avatar_url: string | null
  email: string | null
  status: UserStatus
  is_moderator: boolean
  is_core_user: boolean
  vote_weight: number | null
  is_currently_banned: boolean
  points: number
  vip_expiredate: string | null
  /** Explicit permanent entitlement; when true vip_expiredate is always null. */
  vip_is_permanent: boolean
  vip_active: boolean
  created_at: string | null
  last_sign_in_at: string | null
  email_verified_at: string | null
  etiquette_passed_at: string | null
  comprehensive_passed_at: string | null
  is_fully_verified: boolean
}

interface ListResult {
  ok: boolean
  total?: number
  rows?: Array<UserRow>
  error?: string
}

interface UserOverview {
  ok: boolean
  profile?: {
    id: string
    username: string | null
    display_name: string | null
    avatar?: unknown
    avatar_url: string | null
    status: UserStatus
    created_at: string | null
    needs_email_setup?: boolean
    needs_username_change?: boolean
    legacy_user_id?: number | null
    username_changed_at?: string | null
    email_verified_at?: string | null
    etiquette_passed_at?: string | null
    comprehensive_passed_at?: string | null
    moderation_meta?: Record<string, unknown>
  }
  auth?: {
    email: string | null
    email_confirmed_at: string | null
    last_sign_in_at: string | null
    banned_until: string | null
    created_at: string | null
  }
  roles?: {
    is_admin: boolean
    is_moderator: boolean
    is_core_user: boolean
    vote_weight: number
    is_currently_banned: boolean
  }
  exam?: {
    email_verified_at: string | null
    etiquette_passed_at: string | null
    comprehensive_passed_at: string | null
    is_fully_verified: boolean
  }
  membership?: {
    vip_expiredate: string | null
    vip_is_permanent: boolean
    is_active_vip: boolean
  }
  points?: { points: number; points_frozen: number }
  active_ban?: {
    id: number
    user_id: string
    banned_by: string | null
    banned_at: string
    expires_at: string | null
    reason: string
  } | null
  error?: string
}

type QuizPart = 'etiquette' | 'comprehensive'
type SuspendPart = QuizPart | 'both'

/** Row of admin.list_quiz_lockouts (moderation.exam_lockouts). */
interface QuizLockout {
  id: number
  user_id: string
  part: QuizPart
  locked_by: string | null
  locked_at: string
  expires_at: string
  reason: string
  lifted_at: string | null
  lifted_by: string | null
  lift_reason: string | null
}

interface LockoutListResult {
  ok: boolean
  lockouts?: QuizLockout[]
  error?: string
}

interface PresetAvatar {
  id: string
  url: string
  sort_order: number
}

/* ── i18n ───────────────────────────────────────────────────── */

const STATUS_LABELS: Record<string, string> = {
  active: '正常',
  suspended: '暂停',
  banned: '已封禁',
}

const ROLE_FILTER_LABELS: Record<string, string> = {
  moderator: '版主',
  core: '核心用户',
  normal: '普通',
}

const MEMBERSHIP_FILTER_LABELS: Record<MembershipFilter, string> = {
  vip: 'VIP 生效中',
  permanent: '永久会员',
  expired: 'VIP 已过期',
  non_vip: '无 VIP',
}

/** Compact VIP label for list / mobile cards. */
function formatVipLabel(
  row: Pick<UserRow, 'vip_active' | 'vip_is_permanent' | 'vip_expiredate'>,
): {
  kind: 'permanent' | 'active' | 'expired' | 'none'
  text: string
} {
  if (row.vip_is_permanent) {
    return { kind: 'permanent', text: '永久会员' }
  }
  if (row.vip_active && row.vip_expiredate) {
    return {
      kind: 'active',
      text: `VIP 至 ${formatDate(row.vip_expiredate, 'MM-DD')}`,
    }
  }
  if (row.vip_expiredate) {
    return { kind: 'expired', text: 'VIP 已过期' }
  }
  return { kind: 'none', text: '无 VIP' }
}

const SORT_LABELS: Record<SortKey, string> = {
  created_desc: '注册时间倒序',
  last_sign_in_desc: '最近登录倒序',
  username_asc: '用户名字母序',
}

const PART_LABELS: Record<QuizPart, string> = {
  etiquette: '礼仪卷',
  comprehensive: '综合卷',
}

/** 锁综合卷 = 只封弹幕保留评论；锁礼仪卷（或两档）= 评论弹幕都发不了。 */
const PART_EFFECT_LABELS: Record<QuizPart, string> = {
  etiquette: '评论 + 弹幕均受限',
  comprehensive: '仅弹幕受限，评论照常',
}

/* ── helpers ────────────────────────────────────────────────── */

const PAGE_SIZE = 50
const REASON_MIN = 5

function statusColor(s: UserStatus, banned: boolean) {
  if (banned) return 'error'
  switch (s) {
    case 'active':
      return 'success'
    case 'suspended':
      return 'warning'
    case 'banned':
      return 'error'
    default:
      return 'default'
  }
}

function shortUid(uid: string): string {
  return uid.slice(0, 8) + '…'
}

function formatDate(t: string | null | undefined, fmt = 'YYYY-MM-DD HH:mm') {
  return formatCST(t, fmt)
}

/* ── 主组件 ─────────────────────────────────────────────────── */

function UsersPage() {
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [status, setStatus] = useState<UserStatus | undefined>()
  const [role, setRole] = useState<RoleFilter | undefined>()
  const [membership, setMembership] = useState<MembershipFilter | undefined>()
  const [sort, setSort] = useState<SortKey>('created_desc')

  const [detailUid, setDetailUid] = useState<string | null>(null)
  const isMobile = useIsMobile()

  useEffect(() => {
    setPage(1)
  }, [appliedSearch, status, role, membership, sort])

  const listQuery = useQuery({
    queryKey: [
      'admin_users',
      { page, appliedSearch, status, role, membership, sort },
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_users', {
          p_search: appliedSearch || null,
          p_status: status ?? null,
          p_role: role ?? null,
          p_membership: membership ?? null,
          p_sort: sort,
          p_limit: PAGE_SIZE,
          p_offset: (page - 1) * PAGE_SIZE,
        })
      if (error) throw error
      const r = data as ListResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return { rows: r.rows ?? [], total: r.total ?? 0 }
    },
  })

  const columns: ColumnsType<UserRow> = useMemo(
    () => [
      {
        title: '用户',
        render: (_: unknown, row) => (
          <Space size={10}>
            <Avatar
              src={resolveAvatarUrl(supabase, row.avatar, row.avatar_url)}
              size={40}
            >
              {row.display.slice(0, 1)}
            </Avatar>
            <Space direction="vertical" size={0}>
              <Typography.Text style={{ fontSize: 13 }}>
                {row.display}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {row.username ? `@${row.username}` : '—'}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                copyable={{ text: row.id }}
                style={{
                  fontSize: 11,
                  fontFamily: 'var(--ant-font-family-code, monospace)',
                }}
              >
                {shortUid(row.id)}
              </Typography.Text>
            </Space>
          </Space>
        ),
      },
      {
        title: '邮箱',
        dataIndex: 'email',
        width: 220,
        render: (e: string | null) =>
          e ? (
            <Typography.Text
              copyable={{ text: e }}
              style={{ fontSize: 12 }}
              ellipsis
            >
              {e}
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              —
            </Typography.Text>
          ),
      },
      {
        title: '状态 / 身份',
        width: 180,
        render: (_: unknown, row) => (
          <Space size={4} wrap>
            <Tag color={statusColor(row.status, row.is_currently_banned)}>
              {row.is_currently_banned
                ? '已封禁'
                : (STATUS_LABELS[row.status] ?? row.status)}
            </Tag>
            {row.is_moderator ? <Tag color="geekblue">版主</Tag> : null}
            {row.is_core_user ? (
              <Tag color="purple">
                核心
                {row.vote_weight && row.vote_weight > 1
                  ? `×${row.vote_weight}`
                  : ''}
              </Tag>
            ) : null}
          </Space>
        ),
      },
      {
        title: '会员 / 积分',
        width: 180,
        render: (_: unknown, row) => {
          const vip = formatVipLabel(row)
          return (
            <Space direction="vertical" size={0}>
              {vip.kind === 'permanent' ? (
                <Tag color="magenta" style={{ marginRight: 0 }}>
                  {vip.text}
                </Tag>
              ) : vip.kind === 'active' ? (
                <Tag color="gold" style={{ marginRight: 0 }}>
                  {vip.text}
                </Tag>
              ) : vip.kind === 'expired' ? (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 11 }}
                  title={`过期于 ${formatDate(row.vip_expiredate)}`}
                >
                  {vip.text}
                </Typography.Text>
              ) : (
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {vip.text}
                </Typography.Text>
              )}
              <Typography.Text
                style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
              >
                {row.points.toLocaleString('zh-CN')} 积分
              </Typography.Text>
            </Space>
          )
        },
      },
      {
        title: '答题进度',
        width: 110,
        render: (_: unknown, row) => (
          <Space size={4}>
            <QuizStepTag
              label="礼"
              passedAt={row.etiquette_passed_at}
              title="礼仪卷"
            />
            <QuizStepTag
              label="综"
              passedAt={row.comprehensive_passed_at}
              title="综合卷"
            />
          </Space>
        ),
      },
      {
        title: '注册 / 最近登录',
        width: 170,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text style={{ fontSize: 11 }} type="secondary">
              注册 {formatDate(row.created_at, 'YYYY-MM-DD')}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 12 }}>
              登录 {formatDate(row.last_sign_in_at, 'MM-DD HH:mm')}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '操作',
        width: 80,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Button type="link" size="small" onClick={() => setDetailUid(row.id)}>
            详情
          </Button>
        ),
      },
    ],
    [],
  )

  const rows = listQuery.data?.rows ?? []
  const total = listQuery.data?.total ?? 0

  return (
    <>
      <Card
        title="用户"
        extra={
          <Tooltip title={isMobile ? '刷新' : undefined}>
            <Button
              icon={<ReloadOutlined />}
              aria-label="刷新"
              onClick={() => listQuery.refetch()}
              loading={listQuery.isFetching && !listQuery.isLoading}
            >
              {isMobile ? null : '刷新'}
            </Button>
          </Tooltip>
        }
      >
        <AdminFilterBar
          className="users-filter-bar"
          activeCount={
            (status ? 1 : 0) +
            (role ? 1 : 0) +
            (membership ? 1 : 0) +
            (sort !== 'created_desc' ? 1 : 0)
          }
          primary={
            <Input.Search
              allowClear
              placeholder="搜用户名 / 昵称 / 邮箱"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={(v) => setAppliedSearch(v.trim())}
              style={{ width: 260 }}
            />
          }
        >
          <Select
            allowClear
            placeholder="状态"
            value={status}
            onClear={() => setStatus(undefined)}
            onChange={(v) => setStatus(v)}
            style={{ width: 130 }}
            options={Object.entries(STATUS_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
          />
          <Select
            allowClear
            placeholder="身份"
            value={role}
            onClear={() => setRole(undefined)}
            onChange={(v) => setRole(v)}
            style={{ width: 140 }}
            options={Object.entries(ROLE_FILTER_LABELS).map(
              ([value, label]) => ({
                value,
                label,
              }),
            )}
          />
          <Select
            allowClear
            placeholder="会员状态"
            value={membership}
            onClear={() => setMembership(undefined)}
            onChange={(v) => setMembership(v)}
            style={{ width: 160 }}
            options={Object.entries(MEMBERSHIP_FILTER_LABELS).map(
              ([value, label]) => ({ value, label }),
            )}
          />
          <Select
            value={sort}
            onChange={(v) => setSort(v)}
            style={{ width: 200 }}
            options={Object.entries(SORT_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
          />
        </AdminFilterBar>

        <div className="admin-table-wrap">
          <Table<UserRow>
            rowKey="id"
            size="middle"
            loading={listQuery.isLoading}
            columns={columns}
            dataSource={rows}
            onRow={(row) => ({
              onClick: () => setDetailUid(row.id),
              style: { cursor: 'pointer' },
            })}
            scroll={{ x: 1210 }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              showSizeChanger: false,
              showTotal: (totalRows) => `共 ${totalRows} 条`,
              onChange: (p) => setPage(p),
            }}
          />
        </div>

        <AdminMobileList
          rows={rows}
          rowKey={(row) => row.id}
          loading={listQuery.isLoading || listQuery.isFetching}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
          renderItem={(row) => (
            <article
              className="admin-mobile-record"
              role="button"
              tabIndex={0}
              onClick={() => setDetailUid(row.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setDetailUid(row.id)
              }}
            >
              <div className="admin-mobile-record__head">
                <div className="admin-mobile-record__lead">
                  <Avatar
                    src={resolveAvatarUrl(supabase, row.avatar, row.avatar_url)}
                    size={40}
                  >
                    {row.display.slice(0, 1)}
                  </Avatar>
                  <div className="admin-mobile-record__title">
                    <Typography.Text>{row.display}</Typography.Text>
                    <div className="admin-mobile-record__meta">
                      <span>
                        {row.username ? `@${row.username}` : '无用户名'}
                      </span>
                      <span>{shortUid(row.id)}</span>
                    </div>
                  </div>
                </div>
                <Tag color={statusColor(row.status, row.is_currently_banned)}>
                  {row.is_currently_banned
                    ? '已封禁'
                    : (STATUS_LABELS[row.status] ?? row.status)}
                </Tag>
              </div>
              <div className="admin-mobile-record__badges">
                {row.is_moderator ? <Tag color="geekblue">版主</Tag> : null}
                {row.is_core_user ? (
                  <Tag color="purple">
                    核心
                    {row.vote_weight && row.vote_weight > 1
                      ? `×${row.vote_weight}`
                      : ''}
                  </Tag>
                ) : null}
                {(() => {
                  const vip = formatVipLabel(row)
                  if (vip.kind === 'permanent') {
                    return <Tag color="magenta">{vip.text}</Tag>
                  }
                  if (vip.kind === 'active') {
                    return <Tag color="gold">{vip.text}</Tag>
                  }
                  return null
                })()}
                <QuizStepTag
                  label="礼"
                  passedAt={row.etiquette_passed_at}
                  title="礼仪卷"
                />
                <QuizStepTag
                  label="综"
                  passedAt={row.comprehensive_passed_at}
                  title="综合卷"
                />
              </div>
              <div className="admin-mobile-record__body users-mobile-card__email">
                {row.email ?? (
                  <Typography.Text type="secondary">无邮箱</Typography.Text>
                )}
              </div>
              <div className="admin-mobile-record__meta">
                <span>{row.points.toLocaleString('zh-CN')} 积分</span>
                <span>注册 {formatDate(row.created_at, 'YYYY-MM-DD')}</span>
                <span>
                  登录 {formatDate(row.last_sign_in_at, 'MM-DD HH:mm')}
                </span>
              </div>
            </article>
          )}
        />
      </Card>

      <UserDetailDrawer uid={detailUid} onClose={() => setDetailUid(null)} />
    </>
  )
}

/* ── 详情抽屉 ───────────────────────────────────────────────── */

type ActionKind =
  | { kind: 'username'; current: string | null }
  | { kind: 'password' }
  | { kind: 'avatar'; current: string | null }
  | {
      kind: 'membership'
      currentExpiredate: string | null
      isPermanent: boolean
      initialMode?: MembershipMode
    }
  | { kind: 'points'; current: number }
  | { kind: 'reset_quiz' }
  | { kind: 'mod_toggle'; isModerator: boolean }
  | { kind: 'core_toggle'; isCoreUser: boolean; voteWeight: number }
  | { kind: 'ban' }
  | { kind: 'suspend_quiz' }
  | { kind: 'lift_ban'; banId: number }
  | { kind: 'lift_lockout'; lockoutId: number; part: QuizPart }

function UserDetailDrawer({
  uid,
  onClose,
}: {
  uid: string | null
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [action, setAction] = useState<ActionKind | null>(null)
  const isMobile = useIsMobile()

  const overviewQuery = useQuery({
    queryKey: ['user_overview', uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('get_user_overview', { p_target_user_id: uid })
      if (error) throw error
      const r = data as UserOverview
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
  })

  const lockoutsQuery = useQuery({
    queryKey: ['quiz_lockouts', uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_quiz_lockouts', {
          p_target_user_id: uid,
          p_active_only: true,
          p_limit: 50,
        })
      if (error) throw error
      const r = data as LockoutListResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r.lockouts ?? []
    },
  })

  const ov = overviewQuery.data
  const isTargetAdmin = ov?.roles?.is_admin === true
  const profileAvatarUrl = ov?.profile
    ? resolveAvatarUrl(supabase, ov.profile.avatar, ov.profile.avatar_url)
    : null
  const activeBan = ov?.active_ban ?? null
  const lockouts = lockoutsQuery.data ?? []

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['user_overview', uid] })
    qc.invalidateQueries({ queryKey: ['quiz_lockouts', uid] })
    qc.invalidateQueries({ queryKey: ['admin_users'] })
  }

  return (
    <>
      <Drawer
        open={!!uid}
        onClose={onClose}
        width={isMobile ? '100%' : 780}
        className="users-detail-drawer"
        title={
          ov?.profile
            ? `用户 · ${ov.profile.display_name || ov.profile.username || '—'}`
            : '用户详情'
        }
        destroyOnHidden
      >
        {!uid ? null : overviewQuery.isLoading ? (
          <Typography.Text type="secondary">加载中…</Typography.Text>
        ) : !ov?.profile ? (
          <Alert
            type="error"
            message={overviewQuery.error?.message || '加载失败'}
          />
        ) : (
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            {isTargetAdmin ? (
              <Alert
                type="warning"
                showIcon
                message="这是一个管理员账户"
                description="管理员账户不应该出现在 profiles 表里。请检查隔离触发器是否失效。"
              />
            ) : null}
            {ov.roles?.is_currently_banned ? (
              <Alert
                type="error"
                showIcon
                message="该用户当前处于封禁状态"
                description="被封禁的用户仍可登录、观看，只是发不了评论和弹幕。详见下方「处罚」区块。"
              />
            ) : null}

            {/* ── 基本信息 ────────────────────────────────────── */}
            <Section title="基本信息">
              <Descriptions
                column={isMobile ? 1 : 2}
                size="small"
                bordered
                className="users-detail-desc"
                items={[
                  {
                    label: '头像',
                    span: isMobile ? 1 : 2,
                    children: (
                      <Space>
                        <Avatar src={profileAvatarUrl} size={56} />
                        <Button
                          icon={<EditOutlined />}
                          size="small"
                          onClick={() =>
                            setAction({
                              kind: 'avatar',
                              current: profileAvatarUrl,
                            })
                          }
                        >
                          改头像
                        </Button>
                      </Space>
                    ),
                  },
                  {
                    label: '用户名',
                    children: (
                      <Space>
                        <Typography.Text>
                          {ov.profile.username || '—'}
                        </Typography.Text>
                        <Button
                          icon={<EditOutlined />}
                          type="text"
                          size="small"
                          onClick={() =>
                            setAction({
                              kind: 'username',
                              current: ov.profile?.username ?? null,
                            })
                          }
                        />
                      </Space>
                    ),
                  },
                  {
                    label: '昵称',
                    children: ov.profile.display_name || '—',
                  },
                  {
                    label: '邮箱',
                    children: ov.auth?.email ? (
                      <Typography.Text
                        copyable={{ text: ov.auth.email }}
                        style={{ fontSize: 13 }}
                      >
                        {ov.auth.email}
                      </Typography.Text>
                    ) : (
                      '—'
                    ),
                  },
                  {
                    label: 'UID',
                    children: (
                      <Typography.Text
                        copyable
                        className="users-detail-uid"
                        style={{
                          fontSize: 12,
                          fontFamily: 'var(--ant-font-family-code, monospace)',
                        }}
                      >
                        {ov.profile.id}
                      </Typography.Text>
                    ),
                  },
                  {
                    label: '注册时间',
                    children: formatDate(
                      ov.profile.created_at,
                      'YYYY-MM-DD HH:mm:ss',
                    ),
                  },
                  {
                    label: '最近登录',
                    children: formatDate(
                      ov.auth?.last_sign_in_at,
                      'YYYY-MM-DD HH:mm:ss',
                    ),
                  },
                  {
                    label: '账户状态',
                    children: (
                      <Tag
                        color={statusColor(
                          ov.profile.status,
                          !!ov.roles?.is_currently_banned,
                        )}
                      >
                        {ov.roles?.is_currently_banned
                          ? '已封禁'
                          : (STATUS_LABELS[ov.profile.status] ??
                            ov.profile.status)}
                      </Tag>
                    ),
                  },
                  {
                    label: 'Legacy ID',
                    children: ov.profile.legacy_user_id ?? '—',
                  },
                ]}
              />
            </Section>

            {/* ── 账户安全 ────────────────────────────────────── */}
            <Section title="账户安全">
              <Space direction="vertical" size={8}>
                <Typography.Text type="secondary">
                  管理员可为用户设置新密码。操作会写入审计日志，但不会记录密码内容。
                </Typography.Text>
                <Button
                  danger
                  icon={<LockOutlined />}
                  onClick={() => setAction({ kind: 'password' })}
                  disabled={isTargetAdmin}
                >
                  修改密码
                </Button>
              </Space>
            </Section>

            {/* ── 身份 ────────────────────────────────────────── */}
            <Section title="身份与权限">
              <Space size={8} wrap style={{ marginBottom: 12 }}>
                {ov.roles?.is_moderator ? (
                  <Tag color="geekblue">版主</Tag>
                ) : null}
                {ov.roles?.is_core_user ? (
                  <Tag color="purple">
                    核心用户 · 票权 ×{ov.roles.vote_weight}
                  </Tag>
                ) : null}
                {!ov.roles?.is_moderator && !ov.roles?.is_core_user ? (
                  <Typography.Text type="secondary">普通用户</Typography.Text>
                ) : null}
              </Space>
              <Space wrap>
                <Button
                  onClick={() =>
                    setAction({
                      kind: 'mod_toggle',
                      isModerator: !!ov.roles?.is_moderator,
                    })
                  }
                  disabled={isTargetAdmin}
                >
                  {ov.roles?.is_moderator ? '撤销版主' : '授予版主'}
                </Button>
                <Button
                  onClick={() =>
                    setAction({
                      kind: 'core_toggle',
                      isCoreUser: !!ov.roles?.is_core_user,
                      voteWeight: ov.roles?.vote_weight ?? 1,
                    })
                  }
                  disabled={isTargetAdmin}
                >
                  {ov.roles?.is_core_user ? '撤销核心' : '授予核心'}
                </Button>
              </Space>
            </Section>

            {/* ── 处罚 ────────────────────────────────────────── */}
            <Section title="处罚">
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Space
                  wrap
                  className="users-punish-cards"
                  style={{ width: '100%' }}
                >
                  <Card
                    size="small"
                    title="当前封禁"
                    style={{ flex: '1 1 340px' }}
                  >
                    {activeBan ? (
                      <Space
                        direction="vertical"
                        size={8}
                        style={{ width: '100%' }}
                      >
                        <Descriptions
                          column={1}
                          size="small"
                          items={[
                            {
                              label: '原因',
                              children: activeBan.reason,
                            },
                            {
                              label: '封禁时间',
                              children: formatDate(activeBan.banned_at),
                            },
                            {
                              label: '到期时间',
                              children: `${formatDate(activeBan.expires_at) || '永久'}（到期自动恢复，无需重新答题）`,
                            },
                          ]}
                        />
                        <Button
                          danger
                          size="small"
                          disabled={isTargetAdmin}
                          onClick={() =>
                            setAction({
                              kind: 'lift_ban',
                              banId: activeBan.id,
                            })
                          }
                        >
                          提前解封
                        </Button>
                      </Space>
                    ) : (
                      <Typography.Text type="secondary">
                        无活跃封禁
                      </Typography.Text>
                    )}
                  </Card>
                  <Card
                    size="small"
                    title="当前禁考期"
                    style={{ flex: '1 1 340px' }}
                  >
                    {lockoutsQuery.isLoading ? (
                      <Typography.Text type="secondary">
                        加载中…
                      </Typography.Text>
                    ) : lockouts.length === 0 ? (
                      <Typography.Text type="secondary">
                        无活跃禁考期
                      </Typography.Text>
                    ) : (
                      <Space
                        direction="vertical"
                        size={12}
                        style={{ width: '100%' }}
                      >
                        {lockouts.map((l) => (
                          <div
                            key={l.id}
                            style={{
                              border:
                                '1px solid var(--ant-color-border-secondary)',
                              borderRadius: 6,
                              padding: '8px 12px',
                            }}
                          >
                            <Space size={6} wrap>
                              <Tag
                                color={
                                  l.part === 'etiquette' ? 'blue' : 'purple'
                                }
                              >
                                {PART_LABELS[l.part] ?? l.part}
                              </Tag>
                              <Tag>{PART_EFFECT_LABELS[l.part] ?? l.part}</Tag>
                            </Space>
                            <Descriptions
                              column={1}
                              size="small"
                              style={{ marginTop: 4 }}
                              items={[
                                { label: '原因', children: l.reason },
                                {
                                  label: '到期时间',
                                  children: `${formatDate(l.expires_at)}（解锁后仍需重新答题才恢复发言）`,
                                },
                              ]}
                            />
                            <Button
                              type="link"
                              size="small"
                              style={{ paddingLeft: 0 }}
                              onClick={() =>
                                setAction({
                                  kind: 'lift_lockout',
                                  lockoutId: l.id,
                                  part: l.part,
                                })
                              }
                            >
                              允许提前重考
                            </Button>
                          </div>
                        ))}
                      </Space>
                    )}
                  </Card>
                </Space>

                <Space wrap>
                  <Button
                    danger
                    icon={<BlockOutlined />}
                    disabled={isTargetAdmin}
                    onClick={() => setAction({ kind: 'ban' })}
                  >
                    禁言（封禁）
                  </Button>
                  <Button
                    danger
                    disabled={isTargetAdmin}
                    onClick={() => setAction({ kind: 'suspend_quiz' })}
                  >
                    撤销答题资格并禁考
                  </Button>
                </Space>
                <Typography.Paragraph
                  type="secondary"
                  style={{ fontSize: 12, marginBottom: 0 }}
                >
                  处罚原因会直接展示给用户。「禁言」到期自动恢复、无需重考；「禁考」到期后仍需重新通过答题才恢复发言。档位「综合卷」只封弹幕（评论照常），「礼仪卷
                  / 全部」等于全面禁言。
                </Typography.Paragraph>
              </Space>
            </Section>

            {/* ── 会员 ────────────────────────────────────────── */}
            <Section title="会员">
              <Descriptions
                column={1}
                size="small"
                bordered
                className="users-detail-desc"
                items={[
                  {
                    label: '会员状态',
                    children: ov.membership?.vip_is_permanent ? (
                      <Tag color="magenta">永久会员</Tag>
                    ) : ov.membership?.is_active_vip ? (
                      <Tag color="gold">限时 VIP 生效中</Tag>
                    ) : ov.membership?.vip_expiredate ? (
                      <Tag>已过期</Tag>
                    ) : (
                      <Typography.Text type="secondary">无 VIP</Typography.Text>
                    ),
                  },
                  {
                    label: 'VIP 到期',
                    children: ov.membership?.vip_is_permanent ? (
                      <Typography.Text type="secondary">
                        永久有效（无到期日）
                      </Typography.Text>
                    ) : ov.membership?.vip_expiredate ? (
                      <Space>
                        <Typography.Text>
                          {formatDate(
                            ov.membership.vip_expiredate,
                            'YYYY-MM-DD HH:mm',
                          )}
                        </Typography.Text>
                        {ov.membership.is_active_vip ? (
                          <Tag color="gold">生效中</Tag>
                        ) : (
                          <Tag>已过期</Tag>
                        )}
                      </Space>
                    ) : (
                      '—'
                    ),
                  },
                ]}
              />
              <Space wrap style={{ marginTop: 12 }}>
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() =>
                    setAction({
                      kind: 'membership',
                      currentExpiredate: ov.membership?.vip_expiredate ?? null,
                      isPermanent: !!ov.membership?.vip_is_permanent,
                    })
                  }
                  disabled={isTargetAdmin}
                >
                  设置会员
                </Button>
                <Button
                  size="small"
                  onClick={() =>
                    setAction({
                      kind: 'membership',
                      currentExpiredate: ov.membership?.vip_expiredate ?? null,
                      isPermanent: !!ov.membership?.vip_is_permanent,
                      initialMode: 'set_permanent',
                    })
                  }
                  disabled={isTargetAdmin}
                >
                  设为永久
                </Button>
              </Space>
            </Section>

            <div>
              <UserBadgesPanel
                uid={uid}
                userLabel={
                  ov.profile.display_name || ov.profile.username || uid
                }
                vipIsPermanent={ov.membership?.vip_is_permanent ?? null}
              />
              <Divider style={{ margin: '12px 0 0' }} />
            </div>

            {/* ── 积分 ────────────────────────────────────────── */}
            <Section title="积分">
              <Descriptions
                column={isMobile ? 1 : 2}
                size="small"
                bordered
                className="users-detail-desc"
                items={[
                  {
                    label: '可用积分',
                    children: (
                      <Typography.Text
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {(ov.points?.points ?? 0).toLocaleString('zh-CN')}
                      </Typography.Text>
                    ),
                  },
                  {
                    label: '冻结积分',
                    children: (ov.points?.points_frozen ?? 0).toLocaleString(
                      'zh-CN',
                    ),
                  },
                ]}
              />
              <Button
                icon={<EditOutlined />}
                size="small"
                style={{ marginTop: 12 }}
                onClick={() =>
                  setAction({
                    kind: 'points',
                    current: ov.points?.points ?? 0,
                  })
                }
                disabled={isTargetAdmin}
              >
                调整积分
              </Button>
            </Section>

            {/* ── 答题资格 ────────────────────────────────────── */}
            <Section title="答题资格">
              <Descriptions
                column={1}
                size="small"
                bordered
                className="users-detail-desc"
                items={[
                  {
                    label: '邮箱验证',
                    children: formatDate(ov.exam?.email_verified_at),
                  },
                  {
                    label: '礼仪卷通过',
                    children: formatDate(ov.exam?.etiquette_passed_at),
                  },
                  {
                    label: '综合卷通过',
                    children: formatDate(ov.exam?.comprehensive_passed_at),
                  },
                  {
                    label: '完整验证',
                    children: ov.exam?.is_fully_verified ? (
                      <Tag color="success">已通过三档</Tag>
                    ) : (
                      <Tag>未完成</Tag>
                    ),
                  },
                ]}
              />
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 12 }}
                message="「重置答题资格」仅清空通过时间，不设禁考期"
                description="用户清完可以立刻重考，基本没有惩罚效果。需要处罚时请使用上方「处罚」区块的「撤销答题资格并禁考」。"
              />
              <Button
                size="small"
                style={{ marginTop: 12 }}
                onClick={() => setAction({ kind: 'reset_quiz' })}
                disabled={isTargetAdmin}
              >
                重置答题资格
              </Button>
            </Section>
          </Space>
        )}
      </Drawer>

      {/* ── 动作 Modal ─────────────────────────────────── */}
      {action && uid ? (
        <ActionModal
          action={action}
          uid={uid}
          onClose={() => setAction(null)}
          onSuccess={() => {
            setAction(null)
            invalidate()
          }}
        />
      ) : null}
    </>
  )
}

function QuizStepTag({
  label,
  passedAt,
  title,
}: {
  label: string
  passedAt: string | null
  title: string
}) {
  const passed = !!passedAt
  return (
    <Tooltip
      title={passed ? `${title}：${formatDate(passedAt)}` : `${title}：未通过`}
    >
      <Tag
        color={passed ? 'success' : 'default'}
        style={{
          marginRight: 0,
          minWidth: 24,
          textAlign: 'center',
          opacity: passed ? 1 : 0.5,
        }}
      >
        {label}
      </Tag>
    </Tooltip>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div>
      <Typography.Title level={5} style={{ marginBottom: 8 }}>
        {title}
      </Typography.Title>
      {children}
      <Divider style={{ margin: '12px 0 0' }} />
    </div>
  )
}

async function readPasswordChangeError(error: unknown): Promise<string> {
  const response = (error as { context?: unknown }).context
  if (response instanceof Response) {
    try {
      const body = (await response.json()) as { error?: unknown }
      if (typeof body.error === 'string') return body.error
    } catch {
      // Fall through to the stable client-side error code.
    }
  }
  return 'password_update_failed'
}

/* ── 动作 Modal（按 kind 切换内容） ──────────────────────────── */

function ActionModal({
  action,
  uid,
  onClose,
  onSuccess,
}: {
  action: ActionKind
  uid: string
  onClose: () => void
  onSuccess: () => void
}) {
  const { message, modal } = AntdApp.useApp()
  const [reason, setReason] = useState('')

  // 表单值（按 kind 不同语义）
  const [usernameInput, setUsernameInput] = useState(
    action.kind === 'username' ? (action.current ?? '') : '',
  )
  const [passwordInput, setPasswordInput] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [selectedAvatarId, setSelectedAvatarId] = useState<string | null>(null)
  const [membershipMode, setMembershipMode] = useState<MembershipMode>(
    action.kind === 'membership'
      ? (action.initialMode ??
          (action.isPermanent ? 'set_permanent' : 'add_days'))
      : 'add_days',
  )
  const [membershipDays, setMembershipDays] = useState<number>(30)
  const [membershipUntil, setMembershipUntil] = useState<Dayjs | null>(null)
  const [pointsDelta, setPointsDelta] = useState<number>(0)
  const [voteWeight, setVoteWeight] = useState<number>(
    action.kind === 'core_toggle' ? action.voteWeight : 1,
  )
  const [banDays, setBanDays] = useState<number>(3)
  const [suspendDays, setSuspendDays] = useState<number>(7)
  const [suspendPart, setSuspendPart] = useState<SuspendPart>('comprehensive')

  const reasonValid = reason.trim().length >= REASON_MIN
  const daysValid = (days: number) => days >= 1 && days <= 365
  const passwordValid =
    passwordInput.length >= 8 &&
    passwordInput.length <= 256 &&
    passwordInput === passwordConfirm

  const mutation = useMutation({
    mutationFn: async () => {
      if (!reasonValid) throw new Error(`reason_too_short`)

      const sb = supabase as any
      const rpc = (name: string, params: Record<string, unknown>) =>
        sb
          .schema('admin')
          .rpc(name, params)
          .then(({ data, error }: { data: unknown; error: unknown }) => {
            if (error) throw error
            const r = data as { ok: boolean; error?: string }
            if (!r.ok) throw new Error(r.error ?? 'unknown')
            return r
          })

      switch (action.kind) {
        case 'username':
          if (!usernameInput.trim()) throw new Error('empty_username')
          return rpc('update_username', {
            p_target_user_id: uid,
            p_new_username: usernameInput.trim(),
            p_reason: reason.trim(),
          })
        case 'password': {
          if (passwordInput.length < 8) throw new Error('password_too_short')
          if (passwordInput !== passwordConfirm) {
            throw new Error('password_mismatch')
          }
          const { data, error } = await supabase.functions.invoke(
            'admin-set-user-password',
            {
              body: {
                target_user_id: uid,
                new_password: passwordInput,
                reason: reason.trim(),
              },
            },
          )
          if (error) throw new Error(await readPasswordChangeError(error))
          const result = data as {
            ok?: boolean
            error?: string
            audit_finalized?: boolean
          } | null
          if (result?.ok !== true) {
            throw new Error(result?.error ?? 'password_update_failed')
          }
          return result
        }
        case 'avatar':
          if (!selectedAvatarId) throw new Error('no_avatar_selected')
          return rpc('update_avatar', {
            p_target_user_id: uid,
            p_preset_avatar_id: selectedAvatarId,
            p_reason: reason.trim(),
          })
        case 'membership':
          return rpc('set_membership', {
            p_target_user_id: uid,
            p_mode: membershipMode,
            p_days: membershipMode === 'add_days' ? membershipDays : null,
            p_until:
              membershipMode === 'set_until'
                ? pickerToUTCISO(membershipUntil)
                : null,
            p_reason: reason.trim(),
          })
        case 'points':
          if (pointsDelta === 0) throw new Error('delta_zero')
          return rpc('adjust_points', {
            p_target_user_id: uid,
            p_delta: pointsDelta,
            p_reason: reason.trim(),
          })
        case 'reset_quiz':
          return rpc('reset_quiz_eligibility', {
            p_target_user_id: uid,
            p_reason: reason.trim(),
          })
        case 'ban':
          if (!daysValid(banDays)) throw new Error('invalid_duration')
          return rpc('ban_user', {
            p_target_user_id: uid,
            p_days: banDays,
            p_reason: reason.trim(),
          })
        case 'suspend_quiz':
          if (!daysValid(suspendDays)) throw new Error('invalid_duration')
          return rpc('suspend_quiz_access', {
            p_target_user_id: uid,
            p_part: suspendPart,
            p_days: suspendDays,
            p_reason: reason.trim(),
          })
        case 'lift_ban':
          return rpc('lift_ban', {
            p_ban_id: action.banId,
            p_lift_reason: reason.trim(),
          })
        case 'lift_lockout':
          return rpc('lift_quiz_lockout', {
            p_lockout_id: action.lockoutId,
            p_lift_reason: reason.trim(),
          })
        case 'mod_toggle':
          return rpc(
            action.isModerator ? 'revoke_moderator' : 'grant_moderator',
            action.isModerator
              ? { p_target_user_id: uid, p_revoke_reason: reason.trim() }
              : { p_target_user_id: uid, p_reason: reason.trim() },
          )
        case 'core_toggle':
          return rpc(
            action.isCoreUser ? 'revoke_core_user' : 'grant_core_user',
            action.isCoreUser
              ? { p_target_user_id: uid, p_revoke_reason: reason.trim() }
              : {
                  p_target_user_id: uid,
                  p_vote_weight: voteWeight,
                  p_reason: reason.trim(),
                },
          )
      }
    },
    onSuccess: (result) => {
      if (
        action.kind === 'password' &&
        typeof result === 'object' &&
        result !== null &&
        'audit_finalized' in result &&
        result.audit_finalized === false
      ) {
        message.warning('密码已修改，但审计状态未完成，请立即检查 Edge 日志')
      } else {
        const okTexts: Partial<Record<ActionKind['kind'], string>> = {
          password: '密码已修改',
          ban: '已封禁',
          suspend_quiz: '已撤销答题资格并挂禁考锁',
          lift_ban: '已解封',
          lift_lockout: '已允许提前重考',
        }
        message.success(okTexts[action.kind] ?? '已保存')
      }
      onSuccess()
    },
    onError: (e: Error) => {
      if (e.message === 'reason_too_short') {
        message.error(`备注必须 ≥ ${REASON_MIN} 字`)
      } else if (e.message === 'no_avatar_selected') {
        message.error('请选择一个预设头像')
      } else if (e.message === 'delta_zero') {
        message.error('调整量不能为 0')
      } else if (e.message === 'empty_username') {
        message.error('用户名不能为空')
      } else if (e.message === 'membership_is_permanent') {
        message.error('该用户已是永久会员，请先清除或改为限时，再增加天数')
      } else if (e.message === 'invalid_days') {
        message.error('天数不能为 0')
      } else if (e.message === 'invalid_mode') {
        message.error('不支持的会员模式')
      } else if (
        e.message === 'password_too_short' ||
        e.message === 'invalid_password'
      ) {
        message.error('新密码至少需要 8 个字符')
      } else if (e.message === 'password_mismatch') {
        message.error('两次输入的密码不一致')
      } else if (e.message === 'weak_password') {
        message.error('密码强度不足或已出现在泄露密码库中')
      } else if (e.message === 'same_password') {
        message.error('新密码不能与当前密码相同')
      } else if (e.message === 'target_is_admin') {
        message.error('不能通过用户管理修改管理员账户密码')
      } else if (e.message === 'user_not_found') {
        message.error('用户不存在')
      } else if (e.message === 'invalid_duration') {
        message.error('天数必须在 1–365 之间')
      } else if (e.message === 'invalid_part') {
        message.error('不支持的答题档位')
      } else if (e.message === 'not_active_ban') {
        message.error('该封禁已失效或已解除，请刷新后重试')
      } else if (e.message === 'not_active_lockout') {
        message.error('该禁考期已失效或已解除，请刷新后重试')
      } else if (
        e.message === 'password_update_failed' ||
        e.message === 'password_update_unavailable'
      ) {
        message.error('密码修改失败，请检查 Auth 与 Edge 日志')
      } else {
        message.error(e.message || '保存失败')
      }
    },
  })

  const titles: Record<ActionKind['kind'], string> = {
    username: '改用户名',
    password: '修改用户密码',
    avatar: '改头像',
    membership: '设置会员',
    points: '调整积分',
    reset_quiz: '重置答题资格',
    mod_toggle:
      action.kind === 'mod_toggle' && action.isModerator
        ? '撤销版主'
        : '授予版主',
    core_toggle:
      action.kind === 'core_toggle' && action.isCoreUser
        ? '撤销核心用户'
        : '授予核心用户',
    ban: '禁言（封禁）',
    suspend_quiz: '撤销答题资格并禁考',
    lift_ban: '提前解封',
    lift_lockout: '允许提前重考',
  }

  return (
    <Modal
      open
      onCancel={onClose}
      onOk={() => {
        if (action.kind !== 'membership') {
          return mutation.mutateAsync()
        }
        if (membershipMode === 'set_permanent') {
          return new Promise<void>((resolve, reject) => {
            modal.confirm({
              title: '设为永久会员',
              content: '剩余天数作废，不可自动退回。',
              okText: '确认设为永久',
              cancelText: '取消',
              onOk: () =>
                mutation.mutateAsync().then(resolve, (err: unknown) => {
                  reject(err)
                  return Promise.reject(err)
                }),
              onCancel: () => reject(),
            })
          })
        }
        if (
          action.isPermanent &&
          (membershipMode === 'set_until' || membershipMode === 'clear')
        ) {
          return new Promise<void>((resolve, reject) => {
            modal.confirm({
              title:
                membershipMode === 'clear' ? '清除永久会员' : '改为限时会员',
              content: '将失去永久身份。',
              okText: membershipMode === 'clear' ? '确认清除' : '确认改到期',
              cancelText: '取消',
              okButtonProps: { danger: true },
              onOk: () =>
                mutation.mutateAsync().then(resolve, (err: unknown) => {
                  reject(err)
                  return Promise.reject(err)
                }),
              onCancel: () => reject(),
            })
          })
        }
        return mutation.mutateAsync()
      }}
      title={titles[action.kind]}
      confirmLoading={mutation.isPending}
      okText={
        action.kind === 'password'
          ? '确认修改'
          : action.kind === 'ban'
            ? '确认禁言'
            : action.kind === 'suspend_quiz'
              ? '确认处罚'
              : action.kind === 'lift_ban'
                ? '确认解封'
                : action.kind === 'lift_lockout'
                  ? '确认提前重考'
                  : '保存'
      }
      cancelText="取消"
      okButtonProps={{
        danger:
          action.kind === 'password' ||
          action.kind === 'ban' ||
          action.kind === 'suspend_quiz' ||
          (action.kind === 'membership' &&
            (membershipMode === 'clear' ||
              (action.isPermanent && membershipMode === 'set_until'))),
        disabled:
          !reasonValid ||
          (action.kind === 'password' && !passwordValid) ||
          (action.kind === 'avatar' && !selectedAvatarId) ||
          (action.kind === 'username' && !usernameInput.trim()) ||
          (action.kind === 'points' && pointsDelta === 0) ||
          (action.kind === 'membership' &&
            membershipMode === 'set_until' &&
            !membershipUntil) ||
          (action.kind === 'ban' && !daysValid(banDays)) ||
          (action.kind === 'suspend_quiz' && !daysValid(suspendDays)),
      }}
      width={
        action.kind === 'avatar'
          ? 640
          : action.kind === 'membership'
            ? 520
            : 480
      }
      destroyOnHidden
    >
      <Form layout="vertical" style={{ marginTop: 8 }}>
        {action.kind === 'username' ? (
          <Form.Item label="新用户名" required>
            <Input
              value={usernameInput}
              onChange={(e) => setUsernameInput(e.target.value)}
              placeholder="字母数字下划线"
            />
          </Form.Item>
        ) : null}

        {action.kind === 'password' ? (
          <>
            <Alert
              type="warning"
              showIcon
              message="保存后用户必须使用新密码登录"
              description="密码不会显示在审计日志中。已经签发的访问令牌不会立即失效。"
              style={{ marginBottom: 12 }}
            />
            <Form.Item
              label="新密码"
              required
              validateStatus={
                passwordInput && passwordInput.length < 8 ? 'error' : undefined
              }
              help={
                passwordInput && passwordInput.length < 8
                  ? '至少 8 个字符'
                  : undefined
              }
            >
              <Input.Password
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                autoComplete="new-password"
                maxLength={256}
                placeholder="至少 8 个字符"
              />
            </Form.Item>
            <Form.Item
              label="再次输入新密码"
              required
              validateStatus={
                passwordConfirm && passwordInput !== passwordConfirm
                  ? 'error'
                  : undefined
              }
              help={
                passwordConfirm && passwordInput !== passwordConfirm
                  ? '两次输入不一致'
                  : undefined
              }
            >
              <Input.Password
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                autoComplete="new-password"
                maxLength={256}
              />
            </Form.Item>
          </>
        ) : null}

        {action.kind === 'avatar' ? (
          <Form.Item label="选择预设头像" required>
            <PresetAvatarPicker
              currentUrl={action.current}
              selectedId={selectedAvatarId}
              onSelect={setSelectedAvatarId}
            />
          </Form.Item>
        ) : null}

        {action.kind === 'membership' ? (
          <>
            <Alert
              type={action.isPermanent ? 'warning' : 'info'}
              showIcon
              style={{ marginBottom: 12 }}
              message={
                action.isPermanent
                  ? '当前为永久会员（无到期日）'
                  : action.currentExpiredate
                    ? `当前到期：${formatDate(action.currentExpiredate, 'YYYY-MM-DD HH:mm')}`
                    : '当前无 VIP'
              }
              description={
                action.isPermanent
                  ? '永久会员不能直接「增加天数」。若要改成限时，请先「清除」或「设定到期」；若要保持永久可直接关闭。'
                  : '永久会员写入 vip_is_permanent=true，到期日会被清空；限时 VIP 只写 vip_expiredate。'
              }
            />
            <Form.Item label="模式" required>
              <Select<MembershipMode>
                value={membershipMode}
                onChange={setMembershipMode}
                options={[
                  {
                    value: 'add_days',
                    label: '增加天数',
                    disabled: action.isPermanent,
                  },
                  { value: 'set_until', label: '设定到期（限时）' },
                  { value: 'set_permanent', label: '设为永久会员' },
                  { value: 'clear', label: '清除（设为无 VIP）' },
                ]}
              />
            </Form.Item>
            {membershipMode === 'add_days' ? (
              <Form.Item label="天数（正数加，负数减；减到 0 以下会被清空）">
                <InputNumber
                  value={membershipDays}
                  onChange={(v) => setMembershipDays(v ?? 0)}
                  style={{ width: 200 }}
                />
              </Form.Item>
            ) : null}
            {membershipMode === 'set_until' ? (
              <Form.Item label="到期时间" required>
                <DatePicker
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm"
                  value={membershipUntil}
                  onChange={setMembershipUntil}
                  style={{ width: 240 }}
                />
              </Form.Item>
            ) : null}
            {membershipMode === 'set_permanent' ? (
              <Alert
                type="success"
                showIcon
                message="将授予永久会员"
                description="保存后到期日清空，用户持续享有 VIP 权益，直至管理员清除或改为限时。"
              />
            ) : null}
          </>
        ) : null}

        {action.kind === 'points' ? (
          <Form.Item label="调整量（正数为加，负数为减）" required>
            <InputNumber
              value={pointsDelta}
              onChange={(v) => setPointsDelta(v ?? 0)}
              style={{ width: 200 }}
              step={100}
            />
          </Form.Item>
        ) : null}

        {action.kind === 'reset_quiz' ? (
          <Alert
            type="warning"
            showIcon
            message="将清空 etiquette 与 comprehensive 两档通过时间"
            description="注意：此操作不设禁考期，用户清完可以立刻重考。需要处罚效果请改用「撤销答题资格并禁考」。"
            style={{ marginBottom: 12 }}
          />
        ) : null}

        {action.kind === 'ban' ? (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`将禁言 ${banDays} 天`}
              description="到期自动恢复，无需重新答题。封禁期间用户仍可登录、观看。处罚原因会直接展示给用户。"
            />
            <Form.Item
              label="封禁天数（1–365）"
              required
              validateStatus={!daysValid(banDays) ? 'error' : undefined}
              help={!daysValid(banDays) ? '天数必须在 1–365 之间' : undefined}
            >
              <InputNumber
                min={1}
                max={365}
                value={banDays}
                onChange={(v) => setBanDays(v ?? 1)}
                style={{ width: 200 }}
              />
            </Form.Item>
          </>
        ) : null}

        {action.kind === 'suspend_quiz' ? (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="将撤销对应档位答题资格并挂禁考锁"
              description="锁定期间不能答题，到期后仍需重新通过答题才恢复发言。处罚原因会直接展示给用户。"
            />
            <Form.Item label="档位" required>
              <Select<SuspendPart>
                value={suspendPart}
                onChange={setSuspendPart}
                options={[
                  {
                    value: 'etiquette',
                    label: '礼仪卷（评论 + 弹幕都发不了）',
                  },
                  {
                    value: 'comprehensive',
                    label: '综合卷（只封弹幕，评论照常）',
                  },
                  { value: 'both', label: '全部（两档都锁，全面禁言）' },
                ]}
              />
            </Form.Item>
            <Form.Item
              label="禁考天数（1–365）"
              required
              validateStatus={!daysValid(suspendDays) ? 'error' : undefined}
              help={
                !daysValid(suspendDays) ? '天数必须在 1–365 之间' : undefined
              }
            >
              <InputNumber
                min={1}
                max={365}
                value={suspendDays}
                onChange={(v) => setSuspendDays(v ?? 1)}
                style={{ width: 200 }}
              />
            </Form.Item>
          </>
        ) : null}

        {action.kind === 'lift_ban' ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="将提前解除该用户的封禁"
            description="解封后用户可立即恢复发评论与弹幕，无需重新答题。"
          />
        ) : null}

        {action.kind === 'lift_lockout' ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="将允许该用户提前重考"
            description={`解除「${PART_LABELS[action.part] ?? action.part}」禁考锁。注意：这只解除禁考期，不恢复已撤销的答题资格——用户仍须重新通过答题才能拿回权限。`}
          />
        ) : null}

        {action.kind === 'mod_toggle' && !action.isModerator ? (
          <Alert
            type="info"
            showIcon
            message="授予版主权限。该用户将获得 moderation actions 入口。"
            style={{ marginBottom: 12 }}
          />
        ) : null}
        {action.kind === 'mod_toggle' && action.isModerator ? (
          <Alert
            type="warning"
            showIcon
            message="撤销版主权限（软删，可再授予复活）"
            style={{ marginBottom: 12 }}
          />
        ) : null}

        {action.kind === 'core_toggle' && !action.isCoreUser ? (
          <Form.Item label="票权 vote_weight（1–20）" required>
            <InputNumber
              value={voteWeight}
              onChange={(v) => setVoteWeight(v ?? 1)}
              min={1}
              max={20}
              style={{ width: 200 }}
            />
          </Form.Item>
        ) : null}
        {action.kind === 'core_toggle' && action.isCoreUser ? (
          <Alert
            type="warning"
            showIcon
            message="撤销核心用户身份（软删）"
            style={{ marginBottom: 12 }}
          />
        ) : null}

        <Form.Item
          label={
            action.kind === 'lift_ban'
              ? `解封原因（≥ ${REASON_MIN} 字，会写入审计日志）`
              : action.kind === 'lift_lockout'
                ? `提前重考原因（≥ ${REASON_MIN} 字，会写入审计日志）`
                : action.kind === 'ban' || action.kind === 'suspend_quiz'
                  ? `处罚原因（≥ ${REASON_MIN} 字，会直接展示给用户，请勿填内部黑话）`
                  : `备注（≥ ${REASON_MIN} 字，会写入审计日志）`
          }
          required
          validateStatus={reason && !reasonValid ? 'error' : undefined}
          help={
            reason && !reasonValid
              ? `还差 ${REASON_MIN - reason.trim().length} 字`
              : undefined
          }
        >
          <Input.TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

/* ── 预设头像 Picker ────────────────────────────────────────── */

function PresetAvatarPicker({
  currentUrl,
  selectedId,
  onSelect,
}: {
  currentUrl: string | null
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  const isMobile = useIsMobile()
  const q = useQuery<PresetAvatar[]>({
    queryKey: ['preset_avatars'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('preset_avatars')
        .select('id, url, sort_order')
        .order('sort_order', { ascending: true })
      if (error) throw error
      return data
    },
    staleTime: 5 * 60_000,
  })

  if (q.isLoading)
    return <Typography.Text type="secondary">加载中…</Typography.Text>

  // 桌面固定 8 列；手机按可用宽度自动排列，避免 8×56px 撑破弹窗
  const gridTemplateColumns = isMobile
    ? 'repeat(auto-fill, minmax(56px, 1fr))'
    : 'repeat(8, 1fr)'

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          当前头像：
        </Typography.Text>
        <Avatar src={currentUrl} size={32} style={{ marginLeft: 8 }} />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns,
          gap: 8,
          maxHeight: 320,
          overflowY: 'auto',
          padding: 8,
          border: '1px solid var(--ant-color-border)',
          borderRadius: 4,
        }}
      >
        {(q.data ?? []).map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onSelect(a.id)}
            style={{
              border:
                selectedId === a.id
                  ? '2px solid var(--ant-color-primary)'
                  : '2px solid transparent',
              borderRadius: 4,
              padding: 0,
              cursor: 'pointer',
              background: 'transparent',
            }}
          >
            <Image
              src={a.url}
              alt={`头像 ${a.sort_order}`}
              width={56}
              height={56}
              preview={false}
              style={{ borderRadius: 4, objectFit: 'cover' }}
            />
          </button>
        ))}
      </div>
    </div>
  )
}
